import type { ReactNode } from "react";
import { createContext, useMemo } from "react";
import { browserLocale } from "../utils/date/dateLocale";
import { translate } from "./translate";
import type {
  KitTranslationFunction,
  SupportedLanguage,
  TranslationFunction,
} from "./types";
import { isRTL } from "./types";

interface ComponentsTranslationsContextValue {
  language: SupportedLanguage;
  /** Date order and first weekday come from here; names from `language`. */
  formatLocale: string;
  t: KitTranslationFunction;
  rtl: boolean;
}

// Without a provider the kit still renders English strings, never raw keys.
const TranslationsContext = createContext<ComponentsTranslationsContextValue>({
  language: "en",
  formatLocale: browserLocale(),
  t: translate,
  rtl: false,
});

type ComponentsLocaleProviderProps = {
  language: SupportedLanguage;
  // The app's translator. It must know the kit's keys: register `uiTranslations` with it.
  t?: TranslationFunction;
  /** The tag dates are formatted by. Defaults to the browser's; a user setting may override it. */
  formatLocale?: string;
  children: ReactNode;
};

export function ComponentsLocaleProvider({
  language,
  t = translate,
  formatLocale,
  children,
}: ComponentsLocaleProviderProps) {
  const value = useMemo(
    () => ({
      language,
      formatLocale: formatLocale ?? browserLocale(),
      t,
      rtl: isRTL(language),
    }),
    [language, formatLocale, t],
  );

  return <TranslationsContext value={value}>{children}</TranslationsContext>;
}

export { TranslationsContext };
